// src/App.js
import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Layout from './components/Layout';
import LoadingPage from './pages/LoadingPage';
import NotFound from './components/NotFound';
import { ErrorBoundary } from './ErrorBoundary'; // Fixed import to use named export
import { initClientOptimizations } from './utils/criticalCss';
import GoogleAnalytics from './components/GoogleAnalytics'; // Import Google Analytics tracker
import GoogleOneTap from './components/GoogleOneTap'; // Import Google One Tap sign-in
import ScrollToTop from './components/ScrollToTop'; // Import scroll to top component
import Toast from './components/Toast'; // Import Toast for One Tap notifications
import AnalyticsInitializer from './components/AnalyticsInitializer'; // Import cookieless analytics initializer
// Initialize dynamic URL mapping for automatic category support
import { initializeUrlMapping } from './utils/dynamicUrlMapping';
// Import chunk retry utilities
import { lazyWithRetry, setupGlobalChunkErrorHandler } from './utils/chunkRetry';
// Import specialized blog error boundary
import BlogErrorBoundary from './components/blog/BlogErrorBoundary';
import BlogPublicationGuard from './components/blog/BlogPublicationGuard';
// Import environment utility
import { getEnv } from './utils/env';
import { getExpectedGoogleClientId } from './utils/googleAuth';

import './App.css';
import './Theme.css';
import './styles/fontOptimization.css'; // Import font optimization styles

// Import legal pages directly to avoid suspense during prerendering
import PrivacyPolicy from './pages/PrivacyPolicy';
import AccessibilityStatement from './pages/AccessibilityStatement';
import Disclaimer from './components/Disclaimer';
import AffiliateDisclosure from './pages/AffiliateDisclosure';
import VerificationPolicy from './pages/VerificationPolicy';

// Enhanced lazy load other page components with retry logic for chunk loading errors
const Home = lazyWithRetry(() => import('./pages/Home'));
const GlobalHome = lazyWithRetry(() => import('./pages/global/GlobalHome'));
const GlobalAbout = lazyWithRetry(() => import('./pages/global/GlobalAbout'));
const GlobalContact = lazyWithRetry(() => import('./pages/global/GlobalContact'));
const GlobalSearch = lazyWithRetry(() => import('./pages/global/GlobalSearch'));
const GlobalProductList = lazyWithRetry(() => import('./pages/global/GlobalProductList'));
const GlobalProductDetail = lazyWithRetry(() => import('./pages/global/GlobalProductDetail'));
const GlobalFavorites = lazyWithRetry(() => import('./pages/global/GlobalFavorites'));
const GlobalBlog = lazyWithRetry(() => import('./pages/global/GlobalBlog'));
const GlobalPrivacyPolicy = lazyWithRetry(() => import('./pages/global/GlobalPrivacyPolicy'));
const GlobalDoNotSell = lazyWithRetry(() => import('./pages/global/DoNotSell'));
const GlobalDisclaimer = lazyWithRetry(() => import('./pages/global/GlobalDisclaimer'));
const GlobalAccessibilityStatement = lazyWithRetry(() => import('./pages/global/GlobalAccessibilityStatement'));
const GlobalAffiliateDisclosure = lazyWithRetry(() => import('./pages/global/GlobalAffiliateDisclosure'));
const GlobalTopSearchesHub = lazyWithRetry(() => import('./pages/global/GlobalTopSearchesHub'));
// Global Hub Pages
const GlobalHubIndex = lazyWithRetry(() => import('./pages/global/hub/GlobalHubIndex'));
const GlobalCleanBeautyHub = lazyWithRetry(() => import('./pages/global/hub/GlobalCleanBeautyHub'));
const GlobalWellnessHub = lazyWithRetry(() => import('./pages/global/hub/GlobalWellnessHub'));
const GlobalNonToxicHomeHub = lazyWithRetry(() => import('./pages/global/hub/GlobalNonToxicHomeHub'));
const GlobalZeroWasteHub = lazyWithRetry(() => import('./pages/global/hub/GlobalZeroWasteHub'));
const GlobalBabyKidsHub = lazyWithRetry(() => import('./pages/global/hub/GlobalBabyKidsHub'));
const GlobalSeasonalHub = lazyWithRetry(() => import('./pages/global/hub/GlobalSeasonalHub'));
const GlobalBabyRegistry = lazyWithRetry(() => import('./pages/global/GlobalBabyRegistry'));
const GlobalBabyRegistryTemplatePreview = lazyWithRetry(() => import('./pages/global/GlobalBabyRegistryTemplatePreview'));
const About = lazyWithRetry(() => import('./pages/About'));
const Blog = lazyWithRetry(() => import('./pages/Blog'));
const BlogPost = lazyWithRetry(() => import('./pages/BlogPost'));
const CanadianBabyProductsBlog = lazyWithRetry(() => import('./pages/CanadianBabyProductsBlog.js'));
const CanadianBackToSchoolBlog = lazyWithRetry(() => import('./pages/CanadianBackToSchoolBlog.js'));
const BestAntiColicBottles2025 = lazyWithRetry(() => import('./pages/BestAntiColicBottles2025.js'));
const BestNonToxicBabyToysCanada2025 = lazyWithRetry(() => import('./pages/BestNonToxicBabyToysCanada2025.js'));
const OrganicBabyFoodCanada2025 = lazyWithRetry(() => import('./pages/OrganicBabyFoodCanada2025.js'));
const BestBabyWashSensitiveSkinCanada2026 = lazyWithRetry(() => import('./pages/BestBabyWashSensitiveSkinCanada2026.js'));
const BestBabyLotionSensitiveSkinCanada2026 = lazyWithRetry(() => import('./pages/BestBabyLotionSensitiveSkinCanada2026.js'));
const BestFragranceFreeBabyWipesCanada2026 = lazyWithRetry(() => import('./pages/BestFragranceFreeBabyWipesCanada2026.js'));
const BestDiaperCreamsCanadaRashProneSkin2026 = lazyWithRetry(() => import('./pages/BestDiaperCreamsCanadaRashProneSkin2026.js'));
const BestMineralBabySunscreenCanada2026 = lazyWithRetry(() => import('./pages/BestMineralBabySunscreenCanada2026.js'));
const BestDiapersSensitiveSkinCanada2026 = lazyWithRetry(() => import('./pages/BestDiapersSensitiveSkinCanada2026.js'));
const ShopByValuesUSAGuide2025 = lazyWithRetry(() => import('./pages/global/ShopByValuesUSAGuide2025.js'));
const NonToxicBabyEssentialsUSA2025 = lazyWithRetry(() => import('./pages/global/NonToxicBabyEssentialsUSA2025.js'));
const VeganOrganicHalloweenCandy2025 = lazyWithRetry(() => import('./pages/global/VeganOrganicHalloweenCandy2025.js'));
const VeteranOwnedAmazonPicks2025 = lazyWithRetry(() => import('./pages/global/VeteranOwnedAmazonPicks2025.js'));
const ThanksgivingHostessGifts2025 = lazyWithRetry(() => import('./pages/global/ThanksgivingHostessGifts2025.js'));
const NonToxicThanksgivingHostingKit2025 = lazyWithRetry(() => import('./pages/global/NonToxicThanksgivingHostingKit2025.js'));
const NonToxicEcoFriendlyHolidayDecor2025 = lazyWithRetry(() => import('./pages/global/NonToxicEcoFriendlyHolidayDecor2025.js'));
const HolidayTableDecorCenterpieces2025 = lazyWithRetry(() => import('./pages/global/HolidayTableDecorCenterpieces2025.js'));
const BlackFridayCyberMondayHub2025 = lazyWithRetry(() => import('./pages/global/BlackFridayCyberMondayHub2025.js'));
const HowToFindBlackFridayDeals2025 = lazyWithRetry(() => import('./pages/global/HowToFindBlackFridayDeals2025.js'));
// Black Friday blog templates (not in blog listing yet)
const BlackFridayWorthItSplurges2025 = lazyWithRetry(() => import('./pages/global/BlackFridayWorthItSplurges2025.js'));
const BlackFridayKitchenUpgrades2025 = lazyWithRetry(() => import('./pages/global/BlackFridayKitchenUpgrades2025.js'));
const BlackFridaySmallBrands2025 = lazyWithRetry(() => import('./pages/global/BlackFridaySmallBrands2025.js'));
const HolidayOutdoorLightsSafety2025 = lazyWithRetry(() => import('./pages/global/HolidayOutdoorLightsSafety2025.js'));
const PostHolidayStorageReuse2025 = lazyWithRetry(() => import('./pages/global/PostHolidayStorageReuse2025.js'));
const LastMinuteCyberMonday2025 = lazyWithRetry(() => import('./pages/global/LastMinuteCyberMonday2025.js'));
const AirbnbAITrafficArticle2026 = lazyWithRetry(() => import('./pages/global/AirbnbAITrafficArticle2026.js'));
const BestOrganicBabyFoodUSA2026 = lazyWithRetry(() => import('./pages/global/BestOrganicBabyFoodUSA2026.js'));
const BestDiapers2026 = lazyWithRetry(() => import('./pages/global/BestDiapers2026.js'));
const BestNonToxicBabyBottles2026 = lazyWithRetry(() => import('./pages/global/BestNonToxicBabyBottles2026.js'));
const BestNaturalBabySunscreens2026 = lazyWithRetry(() => import('./pages/global/BestNaturalBabySunscreens2026.js'));
const BestBabyLaundryDetergent2026 = lazyWithRetry(() => import('./pages/global/BestBabyLaundryDetergent2026.js'));
const BestBabyWipes2026 = lazyWithRetry(() => import('./pages/global/BestBabyWipes2026.js'));
const BestDiaperRashCreams2026 = lazyWithRetry(() => import('./pages/global/BestDiaperRashCreams2026.js'));
const BestBabyShampoo2026 = lazyWithRetry(() => import('./pages/global/BestBabyShampoo2026.js'));
const BestBabyEczemaMoisturizer2026 = lazyWithRetry(() => import('./pages/global/BestBabyEczemaMoisturizer2026.js'));
const BestNaturalBabyWash2026 = lazyWithRetry(() => import('./pages/global/BestNaturalBabyWash2026.js'));
const BestBabyLotion2026 = lazyWithRetry(() => import('./pages/global/BestBabyLotion2026.js'));
const BestFragranceFreeBabyLotionUSA = lazyWithRetry(() => import('./pages/global/BestFragranceFreeBabyLotionUSA.js'));
const BestBabyFormula2026 = lazyWithRetry(() => import('./pages/global/BestBabyFormula2026.js'));
const BirthdayFavorsLowClutter2026 = lazyWithRetry(() => import('./pages/global/BirthdayFavorsLowClutter2026.js'));
const NonToxicLaundryDetergentAIComparison = lazyWithRetry(() => import('./pages/global/NonToxicLaundryDetergentAIComparison.js'));
const NaturalDishSoapAIComparison = lazyWithRetry(() => import('./pages/global/NaturalDishSoapAIComparison.js'));
const EcoFriendlyDishwasherPodsAIComparison = lazyWithRetry(() => import('./pages/global/EcoFriendlyDishwasherPodsAIComparison.js'));
const EcoFriendlyAllPurposeCleanerAIComparison = lazyWithRetry(() => import('./pages/global/EcoFriendlyAllPurposeCleanerAIComparison.js'));
const NontoxicBathroomCleanerAIComparison = lazyWithRetry(() => import('./pages/global/NontoxicBathroomCleanerAIComparison.js'));
const NontoxicCandleAIComparison = lazyWithRetry(() => import('./pages/global/NontoxicCandleAIComparison.js'));
const NaturalFabricSoftenerAIComparison = lazyWithRetry(() => import('./pages/global/NaturalFabricSoftenerAIComparison.js'));
const NaturalHandSoapAIComparison = lazyWithRetry(() => import('./pages/global/NaturalHandSoapAIComparison.js'));
const NaturalDeodorantAIComparison = lazyWithRetry(() => import('./pages/global/NaturalDeodorantAIComparison.js'));
const ShampooConditionerAIComparison = lazyWithRetry(() => import('./pages/global/ShampooConditionerAIComparison.js'));
const BodyWashAIComparison = lazyWithRetry(() => import('./pages/global/BodyWashAIComparison.js'));
const FaceMoisturizerAIComparison = lazyWithRetry(() => import('./pages/global/FaceMoisturizerAIComparison.js'));
const NaturalSunscreenAIComparison = lazyWithRetry(() => import('./pages/global/NaturalSunscreenAIComparison.js'));
const NaturalFaceCleanserAIComparison = lazyWithRetry(() => import('./pages/global/NaturalFaceCleanserAIComparison.js'));
const NunaBabyGearTenYearReview2026 = lazyWithRetry(() => import('./pages/global/NunaBabyGearTenYearReview2026.js'));
const BestSnowDayPictureBooks2026 = lazyWithRetry(() => import('./pages/global/BestSnowDayPictureBooks2026.js'));
const BestBabyFoodWithoutHeavyMetals2026 = lazyWithRetry(() => import('./pages/global/BestBabyFoodWithoutHeavyMetals2026.js'));
const BestDiapersSensitiveSkin2026 = lazyWithRetry(() => import('./pages/global/BestDiapersSensitiveSkin2026.js'));
const BestAntiColicBabyBottles2026 = lazyWithRetry(() => import('./pages/global/BestAntiColicBabyBottles2026.js'));
const LUBA3AWD1500vs3000vs5000 = lazyWithRetry(() => import('./pages/global/LUBA3AWD1500vs3000vs5000.js'));
const WinbotW3OmniVsW2sOmni = lazyWithRetry(() => import('./pages/global/WinbotW3OmniVsW2sOmni.js'));
const AiperScubaV3vsV3Ultra = lazyWithRetry(() => import('./pages/global/AiperScubaV3vsV3Ultra.js'));
const ISpringWGB32BPFvsPFKSvsPFKDS = lazyWithRetry(() => import('./pages/global/ISpringWGB32BPFvsPFKSvsPFKDS.js'));
const GlobalHomeRobotsHub = lazyWithRetry(() => import('./pages/global/hub/GlobalHomeRobotsHub.js'));
const Contact = lazyWithRetry(() => import('./pages/Contact'));
const Login = lazyWithRetry(() => import('./pages/Login'));
const OneTapDebug = lazyWithRetry(() => import('./pages/OneTapDebug'));
const ProductList = lazyWithRetry(() => import('./pages/ProductList'));

// Global expansion testing component
const GlobalHooksTester = lazyWithRetry(() => import('./components/GlobalHooksTesterClean'));
const APIEndpointDiagnostic = lazyWithRetry(() => import('./components/APIEndpointDiagnostic'));
const StateEndpointDiagnostic = lazyWithRetry(() => import('./components/StateEndpointDiagnostic'));
const ProductDetail = lazyWithRetry(() => import('./pages/ProductDetail'));
const SearchPage = lazyWithRetry(() => import('./pages/SearchPage'));
const AIDiscoverPage = lazyWithRetry(() => import('./pages/AIDiscoverPage'));
const FavoritedProducts = lazyWithRetry(() => import('./pages/FavoritedProducts'));
const Unsubscribe = lazyWithRetry(() => import('./pages/Unsubscribe'));
const BridgePage = lazyWithRetry(() => import('./pages/BridgePage'));
const DefaultRedirect = lazyWithRetry(() => import('./components/DefaultRedirect'));
const UserLogsViewer = lazyWithRetry(() => import('./components/UserLogsViewer'));
const AICitationDashboard = lazyWithRetry(() => import('./components/AICitationDashboard'));
const AdminDashboard = lazyWithRetry(() => import('./pages/global/AdminDashboard'));

// Canadian Brands components with retry logic
const CanadianBrandsHub = lazyWithRetry(() => import('./pages/CanadianBrandsHub'));
const CanadianBrandsByValues = lazyWithRetry(() => import('./pages/CanadianBrandsByValues'));
const CanadianBrandsByValue = lazyWithRetry(() => import('./pages/CanadianBrandsByValue'));
const BrandPage = lazyWithRetry(() => import('./pages/BrandPage'));
const BrandDetail = lazyWithRetry(() => import('./pages/BrandDetail'));

// Global Brands components with retry logic
const USAStateBrands = lazyWithRetry(() => import('./pages/brands/USAStateBrands'));
const GlobalBrandsHub = lazyWithRetry(() => import('./pages/brands/GlobalBrandsHub'));
const GlobalBrandsByValues = lazyWithRetry(() => import('./pages/brands/GlobalBrandsByValues'));
const GlobalBrandsByLocation = lazyWithRetry(() => import('./pages/brands/GlobalBrandsByLocation'));
const BrandSearch = lazyWithRetry(() => import('./pages/brands/BrandSearch'));
const GlobalBrandDetail = lazyWithRetry(() => import('./pages/brands/GlobalBrandDetail'));
const ValueBrands = lazyWithRetry(() => import('./pages/brands/ValueBrands'));

// Global Pricing component with retry logic
const GlobalPricing = lazyWithRetry(() => import('./pages/global/GlobalRegister'));

// Canadian Pricing component with retry logic
const CanadianPricing = lazyWithRetry(() => import('./pages/CanadianRegister'));

// Brand Submission Form component with retry logic
const BrandSubmissionForm = lazyWithRetry(() => import('./pages/global/BrandSubmissionForm'));

// Canadian Brand Submission Form component with retry logic
const CanadianBrandSubmissionForm = lazyWithRetry(() => import('./pages/CanadianBrandSubmissionForm'));

// AI Insights Success/Onboarding page (post-checkout)
const AiInsightsSuccess = lazyWithRetry(() => import('./pages/global/AiInsightsSuccess'));


// Developers / AI Tools page component with retry logic
const Developers = lazyWithRetry(() => import('./pages/global/Developers'));

// Corporate Gifting page component with retry logic
const CorporateGifting = lazyWithRetry(() => import('./pages/global/CorporateGifting'));

// Corporate Gifting Canada page component with retry logic
const CorporateGiftingCanada = lazyWithRetry(() => import('./pages/CorporateGiftingCanada'));

// Verification Policy component with retry logic (Global version)
const GlobalVerificationPolicy = lazyWithRetry(() => import('./pages/global/VerificationPolicy'));

// Terms of Service component with retry logic
const GlobalTermsOfService = lazyWithRetry(() => import('./pages/global/GlobalTermsOfService'));

// AI Visibility for Brands landing page
const AIVisibility = lazyWithRetry(() => import('./pages/global/AIVisibility'));

// AI Visibility Snapshot subscription landing page
const AIVisibilitySnapshot = lazyWithRetry(() => import('./pages/global/AIVisibilitySnapshot'));

// AI Visibility intake form (get-started page before Stripe checkout)
const AIVisibilityForm = lazyWithRetry(() => import('./pages/global/AIVisibilityForm'));

// Canadian AI Visibility Snapshot ($119 CAD one-time / $59 CAD/mo)
const CanadianAIVisibilitySnapshot = lazyWithRetry(() => import('./pages/canadian/CanadianAIVisibilitySnapshot'));

// Top Searches Hub components with retry logic
const TopSearchesHub = lazyWithRetry(() => import('./pages/TopSearchesHub'));
const CanadianSnackBoxes = lazyWithRetry(() => import('./pages/top/CanadianSnackBoxes'));
const MagnesiumGlycinateCanada = lazyWithRetry(() => import('./pages/top/MagnesiumGlycinateCanada'));
const ElectrolytePowdersCanada = lazyWithRetry(() => import('./pages/top/ElectrolytePowdersCanada'));

// Hub pages with retry logic (Canada category hubs)
const HubIndex = lazyWithRetry(() => import('./pages/hub/HubIndex'));
const CleanBeautyHub = lazyWithRetry(() => import('./pages/hub/CleanBeautyHub'));
const WellnessHub = lazyWithRetry(() => import('./pages/hub/WellnessHub'));
const LowWasteHomeHub = lazyWithRetry(() => import('./pages/hub/LowWasteHomeHub'));
const BabyKidsHub = lazyWithRetry(() => import('./pages/hub/BabyKidsHub'));
const HomeRobotsHub = lazyWithRetry(() => import('./pages/hub/HomeRobotsHub'));
const BestRobotLawnMowersCanada2026 = lazyWithRetry(() => import('./pages/BestRobotLawnMowersCanada2026'));
const RTKvsLiDARRobotLawnMowers2026 = lazyWithRetry(() => import('./pages/RTKvsLiDARRobotLawnMowers2026'));
const BestRoboticPoolCleanersCanada2026 = lazyWithRetry(() => import('./pages/BestRoboticPoolCleanersCanada2026'));
const CordlessVsCordedRoboticPoolCleaners2026 = lazyWithRetry(() => import('./pages/CordlessVsCordedRoboticPoolCleaners2026'));
const AiperScubaS1VsDolphinNautilusCCPlus2026 = lazyWithRetry(() => import('./pages/AiperScubaS1VsDolphinNautilusCCPlus2026'));
const EcovacsUltramarineP1VsAiperScubaS12026 = lazyWithRetry(() => import('./pages/EcovacsUltramarineP1VsAiperScubaS12026'));
const AiperIrriSense2VsRainBirdOtoCanada2026 = lazyWithRetry(() => import('./pages/AiperIrriSense2VsRainBirdOtoCanada2026'));
const BestRoboticPoolSkimmersCanada2026 = lazyWithRetry(() => import('./pages/BestRoboticPoolSkimmersCanada2026'));
const BestRobotWindowCleanersCanada2026 = lazyWithRetry(() => import('./pages/BestRobotWindowCleanersCanada2026'));
const AreRobotWindowCleanersSafe2026 = lazyWithRetry(() => import('./pages/AreRobotWindowCleanersSafe2026'));
const WinbotW3OmniVsW2ProOmni2026 = lazyWithRetry(() => import('./pages/WinbotW3OmniVsW2ProOmni2026'));
// Australia blog experiment
const AustraliaBlog = lazyWithRetry(() => import('./pages/AustraliaBlog'));
const WinbotW3OmniVsW2ProOmniAustralia2026 = lazyWithRetry(() => import('./pages/WinbotW3OmniVsW2ProOmniAustralia2026'));
const AreRobotWindowCleanersSafeAustralia2026 = lazyWithRetry(() => import('./pages/AreRobotWindowCleanersSafeAustralia2026'));
const BestRobotWindowCleanersAustralia2026 = lazyWithRetry(() => import('./pages/BestRobotWindowCleanersAustralia2026'));
const BestSelfCleaningLitterBoxesCanada2026 = lazyWithRetry(() => import('./pages/BestSelfCleaningLitterBoxesCanada2026'));
const OpenTopVsEnclosedSelfCleaningLitterBoxes2026 = lazyWithRetry(() => import('./pages/OpenTopVsEnclosedSelfCleaningLitterBoxes2026'));
const LitterRobot4VsNeakasaM1Plus2026 = lazyWithRetry(() => import('./pages/LitterRobot4VsNeakasaM1Plus2026'));
const LitterRobot4VsPetkitPuraMax2Canada2026 = lazyWithRetry(() => import('./pages/LitterRobot4VsPetkitPuraMax2Canada2026'));
const HydroflowPearlVsPearlPlusVsHs40Canada2026 = lazyWithRetry(() => import('./pages/HydroflowPearlVsPearlPlusVsHs40Canada2026'));
const WinbotW2sOmniVsW2ProOmni2026 = lazyWithRetry(() => import('./pages/WinbotW2sOmniVsW2ProOmni2026'));
const BasementMoistureControlHub = lazyWithRetry(() => import('./pages/hub/BasementMoistureControlHub'));
const IdealBasementHumidityCanada2026 = lazyWithRetry(() => import('./pages/IdealBasementHumidityCanada2026'));
const BestBasementDehumidifiersCanada2026 = lazyWithRetry(() => import('./pages/BestBasementDehumidifiersCanada2026'));
const ThreeFivePintVs50PintDehumidifier2026 = lazyWithRetry(() => import('./pages/35PintVs50PintDehumidifier2026'));
const DehumidifierPumpVsGravityDrain2026 = lazyWithRetry(() => import('./pages/DehumidifierPumpVsGravityDrain2026'));
const BestCrawlSpaceDehumidifiersCanada2026 = lazyWithRetry(() => import('./pages/BestCrawlSpaceDehumidifiersCanada2026'));
const BestBatteryBackupSumpPumpsCanada2026 = lazyWithRetry(() => import('./pages/BestBatteryBackupSumpPumpsCanada2026'));
const ZoellerAquanotFit508Canada2026 = lazyWithRetry(() => import('./pages/ZoellerAquanotFit508Canada2026'));
const BatteryBackupVsWaterPoweredSumpPump2026 = lazyWithRetry(() => import('./pages/BatteryBackupVsWaterPoweredSumpPump2026'));
const BestWaterPoweredBackupSumpPumpsCanada2026 = lazyWithRetry(() => import('./pages/BestWaterPoweredBackupSumpPumpsCanada2026'));
const BestSmartSumpPumpMonitorsCanada2026 = lazyWithRetry(() => import('./pages/BestSmartSumpPumpMonitorsCanada2026'));
const BestWaterLeakDetectorsCanada2026 = lazyWithRetry(() => import('./pages/BestWaterLeakDetectorsCanada2026'));
const BestAutomaticWaterShutoffValvesCanada2026 = lazyWithRetry(() => import('./pages/BestAutomaticWaterShutoffValvesCanada2026'));
const MoenFloVsPhynPlusCanada2026 = lazyWithRetry(() => import('./pages/MoenFloVsPhynPlusCanada2026'));
const BestRadonDetectorsCanada2026 = lazyWithRetry(() => import('./pages/BestRadonDetectorsCanada2026'));
const DigitalRadonMonitorVsTestKit2026 = lazyWithRetry(() => import('./pages/DigitalRadonMonitorVsTestKit2026'));
const WinterHomePreparednessHub = lazyWithRetry(() => import('./pages/hub/WinterHomePreparednessHub'));
const BestPortablePowerStationsWinterCanada2026 = lazyWithRetry(() => import('./pages/BestPortablePowerStationsWinterCanada2026'));
const PortablePowerStationSizeWinterOutage2026 = lazyWithRetry(() => import('./pages/PortablePowerStationSizeWinterOutage2026'));
const PortablePowerStationVsGenerator2026 = lazyWithRetry(() => import('./pages/PortablePowerStationVsGenerator2026'));
const AnkerC1000Gen2VsBluettiElite200V2Canada2026 = lazyWithRetry(() => import('./pages/AnkerC1000Gen2VsBluettiElite200V2Canada2026'));
const BestElectricGarageHeatersCanada2026 = lazyWithRetry(() => import('./pages/BestElectricGarageHeatersCanada2026'));
const BestPortableSpaceHeatersCanada2026 = lazyWithRetry(() => import('./pages/BestPortableSpaceHeatersCanada2026'));
const BestFreezeAlarmsCanada2026 = lazyWithRetry(() => import('./pages/BestFreezeAlarmsCanada2026'));
const BestHeatedSnowMeltingMatsCanada2026 = lazyWithRetry(() => import('./pages/BestHeatedSnowMeltingMatsCanada2026'));
const BestPipeHeatingCablesCanada2026 = lazyWithRetry(() => import('./pages/BestPipeHeatingCablesCanada2026'));
const BestRoofGutterDeIcingCablesCanada2026 = lazyWithRetry(() => import('./pages/BestRoofGutterDeIcingCablesCanada2026'));
const BestRoofRakesCanada2026 = lazyWithRetry(() => import('./pages/BestRoofRakesCanada2026'));
const CanadaBabyRegistry = lazyWithRetry(() => import('./pages/CanadaBabyRegistry'));
const CanadaBabyRegistryTemplatePreview = lazyWithRetry(() => import('./pages/CanadaBabyRegistryTemplatePreview'));
const CustomBabyRegistryPage = lazyWithRetry(() => import('./pages/baby-registry/CustomBabyRegistryPage'));
const SustainableBrandsHub = lazyWithRetry(() => import('./pages/hub/SustainableBrandsHub'));
const SeasonalHub = lazyWithRetry(() => import('./pages/hub/SeasonalHub'));

// Collections pages with retry logic
const Collections = lazyWithRetry(() => import('./pages/collections/Collections'));
const PublicCollection = lazyWithRetry(() => import('./pages/collections/PublicCollection'));
const CollectionEditor = lazyWithRetry(() => import('./pages/collections/CollectionEditor'));
const CuratorProfile = lazyWithRetry(() => import('./pages/collections/CuratorProfile'));

// My Values preferences page with retry logic
const MyValues = lazyWithRetry(() => import('./pages/my/MyValues'));

// Curator Application page with retry logic
const CuratorApplication = lazyWithRetry(() => import('./pages/CuratorApplication'));

function App() {
  const location = useLocation();
  const [metaDescription, setMetaDescription] = useState("Discover values-driven brands from Canada and the US. Shop women-owned, veteran-owned, sustainable, and ethical products making a positive impact.");
  const [hydrateComplete, setHydrateComplete] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  // Initialize prerendering state safely to prevent hydration mismatches
  const [isPrerendering, setIsPrerendering] = useState(() => {
    // Only detect prerendering on client-side to avoid SSR/client differences
    return false;
  });
  
  // State for One Tap sign-in toast notification
  const [showOneTapToast, setShowOneTapToast] = useState(false);
  const [oneTapUserName, setOneTapUserName] = useState('');

  // Set up global chunk error handling on app initialization
  useEffect(() => {
    setupGlobalChunkErrorHandler();
  }, []);

  // Disable browser's automatic scroll restoration on app initialization
  useEffect(() => {
    if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Initialize dynamic URL mapping system for automatic category support
  useEffect(() => {
    const initUrlMapping = async () => {
      try {
        await initializeUrlMapping();
        console.log('URL mapping initialized successfully');
      } catch (error) {
        console.warn('Failed to initialize URL mapping:', error);
      }
    };

    initUrlMapping();
  }, []);
  
  useEffect(() => {
    // Check if this is a prerendering environment - only on client side
    if (typeof window !== 'undefined' && typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') {
      setIsPrerendering(true);
      if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-prerendered', 'true');
      }
    }
    
    let description = "Discover values-driven brands from Canada and the US. Shop women-owned, veteran-owned, sustainable, and ethical products making a positive impact.";
    // Set page-specific meta descriptions
    if (location.pathname.includes('/about')) {
      description = "Learn about OriginSelect's mission to connect consumers with values-driven brands from Canada and the US making a positive impact.";
    } else if (location.pathname.includes('/blog')) {
      description = "Read the latest insights, tips, and stories about sustainable, values-driven products from OriginSelect. Discover women-owned, veteran-owned, and ethical brands.";
    } else if (location.pathname.includes('/products') && !location.pathname.includes('/products/')) {
      description = "Explore our curated collection of products from values-driven brands. Shop women-owned, veteran-owned, and sustainable businesses.";
    } else if (location.pathname.includes('/contact')) {
      description = "Get in touch with OriginSelect. We're here to help you discover values-driven brands and answer your questions.";
    } else if (location.pathname.includes('/search')) {
      description = "Search our collection of products from values-driven brands. Find women-owned, veteran-owned, sustainable, and ethical products.";
    } else if (location.pathname.includes('/privacy')) {
      description = "OriginSelect Privacy Policy - Learn how we collect, use, and protect your personal information according to PIPEDA requirements.";
    } else if (location.pathname.includes('/accessibility')) {
      description = "OriginSelect Accessibility Statement - Our commitment to making our website accessible to all users.";
    } else if (location.pathname.includes('/disclaimer')) {
      description = "OriginSelect Disclaimer and Terms of Use - Legal information about using our website and services.";
    } else if (location.pathname.includes('/affiliate-disclosure')) {
      description = "OriginSelect Affiliate Disclosure - Learn about our affiliate relationships and how we maintain transparency while connecting you with values-driven brands.";
    }
    setMetaDescription(description);
  }, [location]);

  // Track route changes
  useEffect(() => {
    // Only scroll to top on navigation changes, not initial load
    if (!isInitialLoad && typeof window !== 'undefined') {
      // Use multiple methods to ensure scroll to top works for all navigation types
      // including browser back/forward buttons
      const scrollToTop = () => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      };

      // Immediate scroll
      requestAnimationFrame(scrollToTop);
      
      // Also force scroll after a short delay to override any browser restoration
      setTimeout(scrollToTop, 10);
    } else {
      setIsInitialLoad(false);
    }

    // Reset the hydration state when route changes
    setHydrateComplete(false);
    
    // Initialize client-side optimizations for the current route
    initClientOptimizations();
    
    // Mark hydration as complete after a short delay
    // This helps ensure smooth transitions between routes
    const timer = setTimeout(() => {
      setHydrateComplete(true);
      
      // Ensure scroll position is at top after content loads
      // This is especially important for back button navigation
      if (!isInitialLoad && typeof window !== 'undefined') {
        const forceScrollToTop = () => {
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        };
        forceScrollToTop();
      }
      
      // Dispatch render-event for prerendering
      if (typeof document !== 'undefined') {
        document.dispatchEvent(new Event('render-event'));
        
        // Add a marker element to indicate content is fully loaded (for PrerenderSPA detection)
        if (!document.getElementById('content-fully-loaded')) {
          const marker = document.createElement('div');
          marker.id = 'content-fully-loaded';
          marker.style.display = 'none';
          document.body.appendChild(marker);
        }
        
        // Set prerender-specific attribute for hydration
        if (document.getElementById('root')) {
          document.getElementById('root').setAttribute('data-prerendered', 'true');
        }
        
        // Special handling for legal pages
        if (
          location.pathname.includes('/privacy') || 
          location.pathname.includes('/disclaimer') || 
          location.pathname.includes('/accessibility') ||
          location.pathname.includes('/affiliate-disclosure')
        ) {
          document.documentElement.setAttribute('data-legal-page', 'true');
        }
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [location.pathname, isInitialLoad]);

  useEffect(() => {
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isInitialLoad]);

  // Enhanced loading component with proper hydration handling
  const LoadingComponent = () => (
    <div className={`app-loading-container ${hydrateComplete ? 'fade-out' : ''}`}>
      <LoadingPage />
    </div>
  );

  // For prerendering environments, render a simpler version without suspense
  if (isPrerendering && (
      location.pathname.includes('/privacy') || 
      location.pathname.includes('/disclaimer') || 
      location.pathname.includes('/accessibility') ||
      location.pathname.includes('/affiliate-disclosure')
    )) {
    let LegalComponent;
    if (location.pathname.includes('/privacy')) {
      LegalComponent = <PrivacyPolicy />;
    } else if (location.pathname.includes('/disclaimer')) {
      LegalComponent = <Disclaimer />;
    } else if (location.pathname.includes('/accessibility')) {
      LegalComponent = <AccessibilityStatement />;
    } else if (location.pathname.includes('/affiliate-disclosure')) {
      LegalComponent = <AffiliateDisclosure />;
    }
    
    return (
      <div className="app legal-prerendered">
        {LegalComponent}
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={getExpectedGoogleClientId()}>
      <HelmetProvider>
        <ErrorBoundary>
          <div className="app">
          {/* Google Analytics tracking component */}
          <GoogleAnalytics />
          {/* Analytics initializer for cookieless tracking */}
          <AnalyticsInitializer />
          {/* Google One Tap sign-in (shows on homepage and key landing pages) */}
          <GoogleOneTap 
            onSuccess={(userData) => {
              console.log('User signed in via One Tap:', userData.email);
              // Show welcome toast only if not shown this session
              if (!sessionStorage.getItem('hasSeenSignInToast')) {
                console.log('🎉 Showing One Tap welcome toast');
                sessionStorage.setItem('hasSeenSignInToast', 'true');
                setOneTapUserName(userData.name?.split(' ')[0] || 'there');
                setShowOneTapToast(true);
              }
            }}
            onError={(error) => {
              console.error('One Tap error:', error);
            }}
            showOnAllPages={false} // Set to true to show on all pages
          />
          {/* Toast notification for One Tap sign-in */}
          {showOneTapToast && (
            <Toast
              message={
                <>
                  <strong style={{ fontSize: '16px' }}>🎉 Welcome, {oneTapUserName}!</strong>
                  <br />
                  Now you can:
                  <br />
                  • ❤️ Save products you love
                  <br />
                  • ✨ Turn favorites into shareable Collections
                  <br />
                  • 🎯 Save your values & see matching products
                </>
              }
              type="success"
              duration={7000}
              onClose={() => {
                console.log('One Tap toast closed');
                setShowOneTapToast(false);
              }}
            />
          )}
          {/* Scroll to top on route changes */}
          <ScrollToTop />
          <Suspense fallback={<LoadingComponent />}>
            <Routes>
              {/* Default redirect handling */}
              <Route path="/" element={<DefaultRedirect />} />

              {/* Bridge page — affiliate-compliant product landing pages for AI agents */}
              <Route path="/go/:slug" element={<BridgePage />} />

              {/* Global expansion routes */}
              <Route path="/global" element={<Layout />}>
                <Route index element={<Navigate to="home" replace />} />
                <Route path="home" element={<GlobalHome />} />
                <Route path="about" element={<GlobalAbout />} />
                <Route path="register" element={<GlobalPricing />} />
                {/* Redirect old pricing URL to new register URL */}
                <Route path="pricing" element={<Navigate to="/global/register" replace />} />
                <Route path="submit-brand/:tier" element={<BrandSubmissionForm />} />
                <Route path="register/success" element={<AiInsightsSuccess marketContext="global" />} />
                <Route path="contact" element={<GlobalContact />} />
                <Route path="corporate-gifting" element={<CorporateGifting />} />
                <Route path="developers" element={<Developers />} />
                <Route path="search" element={<GlobalSearch />} />
                <Route path="discover" element={<AIDiscoverPage />} />
                <Route path="products" element={<GlobalProductList />} />
                <Route path="favorites" element={<GlobalFavorites />} />
                {/* Global Top Searches Hub */}
                <Route path="top" element={<GlobalTopSearchesHub />} />
                {/* Global Hub Pages - Category Hubs */}
                <Route path="hub" element={<GlobalHubIndex />} />
                <Route path="hub/clean-beauty" element={<GlobalCleanBeautyHub />} />
                <Route path="hub/wellness" element={<GlobalWellnessHub />} />
                <Route path="hub/non-toxic-home" element={<GlobalNonToxicHomeHub />} />
                <Route path="hub/zero-waste" element={<GlobalZeroWasteHub />} />
                <Route path="hub/baby-kids" element={<GlobalBabyKidsHub />} />
                <Route path="hub/seasonal" element={<GlobalSeasonalHub />} />
                <Route path="hub/home-robots" element={<GlobalHomeRobotsHub />} />
                {/* Baby Registry Builder landing page */}
                <Route path="baby-registry" element={<GlobalBabyRegistry />} />
                <Route path="baby-registry/:templateSlug" element={<GlobalBabyRegistryTemplatePreview />} />
                <Route path="baby-registry/preview/:templateSlug" element={<GlobalBabyRegistryTemplatePreview />} />
                <Route path="baby-registry/my" element={<CustomBabyRegistryPage />} />
                <Route path="baby-registry/shared/:shareKey" element={<CustomBabyRegistryPage />} />
                {/* Collections routes (global context) */}
                <Route path="collections" element={<Collections />} />
                <Route path="collections/:slugAndId/edit" element={<CollectionEditor />} />
                <Route path="collections/:slugAndId" element={<PublicCollection />} />
                {/* Curator profile routes (global context) */}
                <Route path="curators/:handle" element={<CuratorProfile />} />
                {/* My Values preferences route (global context) */}
                <Route path="my/values" element={<MyValues />} />
                {/* Global product detail pages (mirror country product detail but in global context) */}
                <Route path="products/:productId" element={<GlobalProductDetail />} />
                <Route path="products/:productId/name/:productName" element={<GlobalProductDetail />} />
                {/* Global blog directory */}
                <Route path="blog" element={
                  <BlogErrorBoundary>
                    <GlobalBlog />
                  </BlogErrorBoundary>
                } />
                {/* Global blog - specific posts */}
                <Route path="blog/shop-by-values-usa-guide-2025" element={
                  <BlogErrorBoundary>
                    <ShopByValuesUSAGuide2025 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/non-toxic-baby-essentials-usa-2025" element={
                  <BlogErrorBoundary>
                    <NonToxicBabyEssentialsUSA2025 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/vegan-organic-halloween-candy-2025" element={
                  <BlogErrorBoundary>
                    <VeganOrganicHalloweenCandy2025 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/veteran-owned-amazon-picks-2025" element={
                  <BlogErrorBoundary>
                    <VeteranOwnedAmazonPicks2025 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/thanksgiving-hostess-gifts-2025" element={
                  <BlogErrorBoundary>
                    <ThanksgivingHostessGifts2025 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/non-toxic-thanksgiving-hosting-kit-2025" element={
                  <BlogErrorBoundary>
                    <NonToxicThanksgivingHostingKit2025 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/non-toxic-eco-friendly-holiday-decor-2025" element={
                  <BlogErrorBoundary>
                    <NonToxicEcoFriendlyHolidayDecor2025 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/holiday-table-decor-centerpieces-2025" element={
                  <BlogErrorBoundary>
                    <HolidayTableDecorCenterpieces2025 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/black-friday-cyber-monday-values-led-2025" element={
                  <BlogErrorBoundary>
                    <BlackFridayCyberMondayHub2025 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/how-to-find-black-friday-deals-values" element={
                  <BlogErrorBoundary>
                    <HowToFindBlackFridayDeals2025 />
                  </BlogErrorBoundary>
                } />
                {/* Black Friday blog templates - not in listing yet */}
                <Route path="blog/black-friday-worth-it-splurges-values" element={
                  <BlogErrorBoundary>
                    <BlackFridayWorthItSplurges2025 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/black-friday-kitchen-upgrades-values" element={
                  <BlogErrorBoundary>
                    <BlackFridayKitchenUpgrades2025 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/black-friday-small-brands-values" element={
                  <BlogErrorBoundary>
                    <BlackFridaySmallBrands2025 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/holiday-outdoor-lights-safety-2025" element={
                  <BlogErrorBoundary>
                    <HolidayOutdoorLightsSafety2025 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/post-holiday-storage-reuse-2025" element={
                  <BlogErrorBoundary>
                    <PostHolidayStorageReuse2025 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/last-minute-cyber-monday-values" element={
                  <BlogErrorBoundary>
                    <LastMinuteCyberMonday2025 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/airbnb-ceo-ai-traffic-converts-better-than-google" element={
                  <BlogErrorBoundary>
                    <AirbnbAITrafficArticle2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-organic-baby-food-usa-2026" element={
                  <BlogErrorBoundary>
                    <BestOrganicBabyFoodUSA2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/nuna-baby-gear-10-year-review" element={
                  <BlogErrorBoundary>
                    <NunaBabyGearTenYearReview2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-diapers-2026" element={
                  <BlogErrorBoundary>
                    <BestDiapers2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-non-toxic-baby-bottles-2026" element={
                  <BlogErrorBoundary>
                    <BestNonToxicBabyBottles2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-natural-baby-sunscreens" element={
                  <BlogErrorBoundary>
                    <BestNaturalBabySunscreens2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-baby-laundry-detergent" element={
                  <BlogErrorBoundary>
                    <BestBabyLaundryDetergent2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-baby-wipes" element={
                  <BlogErrorBoundary>
                    <BestBabyWipes2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-diaper-rash-creams" element={
                  <BlogErrorBoundary>
                    <BestDiaperRashCreams2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-baby-shampoo" element={
                  <BlogErrorBoundary>
                    <BestBabyShampoo2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-baby-eczema-moisturizer" element={
                  <BlogErrorBoundary>
                    <BestBabyEczemaMoisturizer2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-natural-baby-wash" element={
                  <BlogErrorBoundary>
                    <BestNaturalBabyWash2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-baby-lotion" element={
                  <BlogErrorBoundary>
                    <BestBabyLotion2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-fragrance-free-baby-lotion-usa" element={
                  <BlogErrorBoundary>
                    <BestFragranceFreeBabyLotionUSA />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-organic-baby-formula" element={
                  <BlogErrorBoundary>
                    <BestBabyFormula2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/birthday-favors-low-clutter-ideas" element={
                  <BlogErrorBoundary>
                    <BirthdayFavorsLowClutter2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/nontoxic-laundry-detergent-ai-comparison" element={
                  <BlogErrorBoundary>
                    <NonToxicLaundryDetergentAIComparison />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/natural-dish-soap-ai-comparison" element={
                  <BlogErrorBoundary>
                    <NaturalDishSoapAIComparison />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/eco-friendly-dishwasher-pods-ai-comparison" element={
                  <BlogErrorBoundary>
                    <EcoFriendlyDishwasherPodsAIComparison />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/eco-friendly-all-purpose-cleaner-ai-comparison" element={
                  <BlogErrorBoundary>
                    <EcoFriendlyAllPurposeCleanerAIComparison />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/nontoxic-bathroom-cleaner-ai-comparison" element={
                  <BlogErrorBoundary>
                    <NontoxicBathroomCleanerAIComparison />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/nontoxic-candle-ai-comparison" element={
                  <BlogErrorBoundary>
                    <NontoxicCandleAIComparison />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/natural-fabric-softener-ai-comparison" element={
                  <BlogErrorBoundary>
                    <NaturalFabricSoftenerAIComparison />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/natural-hand-soap-ai-comparison" element={
                  <BlogErrorBoundary>
                    <NaturalHandSoapAIComparison />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/natural-deodorant-ai-comparison" element={
                  <BlogErrorBoundary>
                    <NaturalDeodorantAIComparison />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/organic-shampoo-conditioner-ai-comparison" element={
                  <BlogErrorBoundary>
                    <ShampooConditionerAIComparison />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/organic-body-wash-ai-comparison" element={
                  <BlogErrorBoundary>
                    <BodyWashAIComparison />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/natural-face-moisturizer-ai-comparison" element={
                  <BlogErrorBoundary>
                    <FaceMoisturizerAIComparison />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/natural-sunscreen-ai-comparison" element={
                  <BlogErrorBoundary>
                    <NaturalSunscreenAIComparison />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/natural-face-cleanser-ai-comparison" element={
                  <BlogErrorBoundary>
                    <NaturalFaceCleanserAIComparison />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-snow-day-picture-books-for-kids-2026" element={
                  <BlogErrorBoundary>
                    <BestSnowDayPictureBooks2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-baby-food-without-heavy-metals-usa-2026" element={
                  <BlogErrorBoundary>
                    <BestBabyFoodWithoutHeavyMetals2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-diapers-for-sensitive-skin-usa" element={
                  <BlogErrorBoundary>
                    <BestDiapersSensitiveSkin2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-anti-colic-baby-bottles-usa" element={
                  <BlogErrorBoundary>
                    <BestAntiColicBabyBottles2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/luba-3-awd-1500-vs-3000-vs-5000" element={
                  <BlogErrorBoundary>
                    <LUBA3AWD1500vs3000vs5000 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/winbot-w3-omni-vs-w2s-omni" element={
                  <BlogErrorBoundary>
                    <WinbotW3OmniVsW2sOmni />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/aiper-scuba-v3-vs-v3-ultra" element={
                  <BlogErrorBoundary>
                    <AiperScubaV3vsV3Ultra />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/ispring-wgb32b-pf-vs-pfks-vs-pfkds" element={
                  <BlogErrorBoundary>
                    <ISpringWGB32BPFvsPFKSvsPFKDS />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/:postSlug" element={
                  <BlogErrorBoundary>
                    <BlogPost />
                  </BlogErrorBoundary>
                } />
                {/* Curator Application */}
                <Route path="curator-application" element={<CuratorApplication />} />
                {/* Global legal pages */}
                <Route path="privacy" element={<GlobalPrivacyPolicy />} />
                <Route path="do-not-sell" element={<GlobalDoNotSell />} />
                <Route path="disclaimer" element={<GlobalDisclaimer />} />
                <Route path="accessibility" element={<GlobalAccessibilityStatement />} />
                <Route path="affiliate-disclosure" element={<GlobalAffiliateDisclosure />} />
                {/* Global brands directory */}
                <Route path="brands" element={<GlobalBrandsHub />} />
                <Route path="brands/search" element={<BrandSearch />} />
                {/* New Browse by Values and Location pages */}
                <Route path="brands/by-values" element={<GlobalBrandsByValues />} />
                <Route path="brands/by-location" element={<GlobalBrandsByLocation />} />
                {/* Brands by value - specific route comes before generic */}
                <Route path="brands/values/:valueId" element={<ValueBrands />} />
                {/* State brands - specific routes for all 50 US states */}
                <Route path="brands/alabama" element={<USAStateBrands />} />
                <Route path="brands/alaska" element={<USAStateBrands />} />
                <Route path="brands/arizona" element={<USAStateBrands />} />
                <Route path="brands/arkansas" element={<USAStateBrands />} />
                <Route path="brands/california" element={<USAStateBrands />} />
                <Route path="brands/colorado" element={<USAStateBrands />} />
                <Route path="brands/connecticut" element={<USAStateBrands />} />
                <Route path="brands/delaware" element={<USAStateBrands />} />
                <Route path="brands/florida" element={<USAStateBrands />} />
                <Route path="brands/georgia" element={<USAStateBrands />} />
                <Route path="brands/hawaii" element={<USAStateBrands />} />
                <Route path="brands/idaho" element={<USAStateBrands />} />
                <Route path="brands/illinois" element={<USAStateBrands />} />
                <Route path="brands/indiana" element={<USAStateBrands />} />
                <Route path="brands/iowa" element={<USAStateBrands />} />
                <Route path="brands/kansas" element={<USAStateBrands />} />
                <Route path="brands/kentucky" element={<USAStateBrands />} />
                <Route path="brands/louisiana" element={<USAStateBrands />} />
                <Route path="brands/maine" element={<USAStateBrands />} />
                <Route path="brands/maryland" element={<USAStateBrands />} />
                <Route path="brands/massachusetts" element={<USAStateBrands />} />
                <Route path="brands/michigan" element={<USAStateBrands />} />
                <Route path="brands/minnesota" element={<USAStateBrands />} />
                <Route path="brands/mississippi" element={<USAStateBrands />} />
                <Route path="brands/missouri" element={<USAStateBrands />} />
                <Route path="brands/montana" element={<USAStateBrands />} />
                <Route path="brands/nebraska" element={<USAStateBrands />} />
                <Route path="brands/nevada" element={<USAStateBrands />} />
                <Route path="brands/new-hampshire" element={<USAStateBrands />} />
                <Route path="brands/new-jersey" element={<USAStateBrands />} />
                <Route path="brands/new-mexico" element={<USAStateBrands />} />
                <Route path="brands/new-york" element={<USAStateBrands />} />
                <Route path="brands/north-carolina" element={<USAStateBrands />} />
                <Route path="brands/north-dakota" element={<USAStateBrands />} />
                <Route path="brands/ohio" element={<USAStateBrands />} />
                <Route path="brands/oklahoma" element={<USAStateBrands />} />
                <Route path="brands/oregon" element={<USAStateBrands />} />
                <Route path="brands/pennsylvania" element={<USAStateBrands />} />
                <Route path="brands/rhode-island" element={<USAStateBrands />} />
                <Route path="brands/south-carolina" element={<USAStateBrands />} />
                <Route path="brands/south-dakota" element={<USAStateBrands />} />
                <Route path="brands/tennessee" element={<USAStateBrands />} />
                <Route path="brands/texas" element={<USAStateBrands />} />
                <Route path="brands/utah" element={<USAStateBrands />} />
                <Route path="brands/vermont" element={<USAStateBrands />} />
                <Route path="brands/virginia" element={<USAStateBrands />} />
                <Route path="brands/washington" element={<USAStateBrands />} />
                <Route path="brands/west-virginia" element={<USAStateBrands />} />
                <Route path="brands/wisconsin" element={<USAStateBrands />} />
                <Route path="brands/wyoming" element={<USAStateBrands />} />
                {/* Individual brand detail pages - must be last */}
                <Route path="brands/:brandId" element={<GlobalBrandDetail />} />
                <Route path="brands/:brandId/:brandSlug" element={<GlobalBrandDetail />} />
                {/* Legal Pages - Global */}
                <Route path="verification-policy" element={<GlobalVerificationPolicy />} />
                <Route path="terms" element={<GlobalTermsOfService />} />
                {/* Partners pages */}
                <Route path="partners/ai-visibility" element={<AIVisibility />} />
                <Route path="partners/ai-visibility-snapshot" element={<AIVisibilitySnapshot />} />
                <Route path="partners/ai-visibility-snapshot/get-started" element={<AIVisibilityForm market="global" />} />
                {/* Admin Dashboard (email-gated inside component) */}
                <Route path="admin" element={<AdminDashboard />} />
                {/* AI Citation Analytics Dashboard */}
                <Route path="ai-citations" element={<AICitationDashboard />} />
                {/* Future global routes will be added here */}
              </Route>

              {/* Standalone routes for privacy and disclaimer, redirecting to a default country version */}
              <Route path="/privacy" element={<Navigate to="/country/canada/privacy" replace />} />
              <Route path="/disclaimer" element={<Navigate to="/country/canada/disclaimer" replace />} />
              <Route path="/accessibility" element={<Navigate to="/country/canada/accessibility" replace />} />
              <Route path="/affiliate-disclosure" element={<Navigate to="/country/canada/affiliate-disclosure" replace />} />
              
              {/* Redirect /verification-policy to global version */}
              <Route path="/verification-policy" element={<Navigate to="/global/verification-policy" replace />} />

              {/* Australia blog experiment — a deliberately narrow, standalone route tree
                  (blog only, no products/brands/registry). Uses a static path rather than
                  being nested under /country/:countryCode so it can't collide with the
                  Canada-only routes below, and so unbuilt AU paths fall through safely to
                  the Canada redirect guard in Layout instead of leaking Canadian content
                  under an Australia URL. See Layout.js's forceCountryCode prop. */}
              <Route path="/country/australia" element={<Layout forceCountryCode="australia" />}>
                <Route index element={<Navigate to="/country/australia/blog" replace />} />
                <Route path="home" element={<Navigate to="/country/australia/blog" replace />} />
                <Route path="blog" element={
                  <BlogErrorBoundary>
                    <AustraliaBlog />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/winbot-w3-omni-vs-w2-pro-omni" element={
                  <BlogErrorBoundary>
                    <WinbotW3OmniVsW2ProOmniAustralia2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/are-robot-window-cleaners-safe" element={
                  <BlogErrorBoundary>
                    <AreRobotWindowCleanersSafeAustralia2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-robot-window-cleaners-australia" element={
                  <BlogErrorBoundary>
                    <BestRobotWindowCleanersAustralia2026 />
                  </BlogErrorBoundary>
                } />
                {/* Legal pages: no Australia-specific policy exists yet. Point at the
                    Global versions (a multi-jurisdiction document) rather than falling
                    through to Canada's PIPEDA-specific privacy policy, which would
                    incorrectly claim Canadian law governs an Australian visitor. */}
                <Route path="privacy" element={<Navigate to="/global/privacy" replace />} />
                <Route path="disclaimer" element={<Navigate to="/global/disclaimer" replace />} />
                <Route path="accessibility" element={<Navigate to="/global/accessibility" replace />} />
                <Route path="affiliate-disclosure" element={<Navigate to="/global/affiliate-disclosure" replace />} />
              </Route>

              {/* Country-specific routes */}
              <Route path="/country/:countryCode" element={<Layout />}>
                {/* The country root is the canonical country home. Keeping Home here
                    prevents a /country/canada → /home redirect loop at the edge. */}
                <Route index element={<Home />} />
                <Route path="home" element={<Home />} />
                <Route path="about" element={<About />} />
                <Route path="register" element={<CanadianPricing />} />
                <Route path="submit-brand/:tier" element={<CanadianBrandSubmissionForm />} />
                <Route path="partners/ai-visibility-snapshot" element={<CanadianAIVisibilitySnapshot />} />
                <Route path="partners/ai-visibility-snapshot/get-started" element={<AIVisibilityForm market="canada" />} />
                <Route path="register/success" element={<AiInsightsSuccess marketContext="canada" />} />
                <Route path="blog" element={
                  <BlogErrorBoundary>
                    <Blog />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/top-canadian-baby-products-2025" element={
                  <BlogErrorBoundary>
                    <CanadianBabyProductsBlog />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/top-canadian-back-to-school-products-2025" element={
                  <BlogErrorBoundary>
                    <CanadianBackToSchoolBlog />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-anti-colic-bottles-canada-2025" element={
                  <BlogErrorBoundary>
                    <BestAntiColicBottles2025 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-non-toxic-baby-toys-canada-2025" element={
                  <BlogErrorBoundary>
                    <BestNonToxicBabyToysCanada2025 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-organic-baby-food-canada-2025" element={
                  <BlogErrorBoundary>
                    <OrganicBabyFoodCanada2025 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-baby-wash-sensitive-skin-canada" element={
                  <BlogErrorBoundary>
                    <BestBabyWashSensitiveSkinCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-baby-lotion-sensitive-skin-canada" element={
                  <BlogErrorBoundary>
                    <BestBabyLotionSensitiveSkinCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-fragrance-free-baby-wipes-canada" element={
                  <BlogErrorBoundary>
                    <BestFragranceFreeBabyWipesCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-diaper-creams-canada-rash-prone-skin" element={
                  <BlogErrorBoundary>
                    <BestDiaperCreamsCanadaRashProneSkin2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-mineral-baby-sunscreen-canada" element={
                  <BlogErrorBoundary>
                    <BestMineralBabySunscreenCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-diapers-sensitive-skin-canada" element={
                  <BlogErrorBoundary>
                    <BestDiapersSensitiveSkinCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-robot-lawn-mowers-canada" element={
                  <BlogErrorBoundary>
                    <BestRobotLawnMowersCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/rtk-vs-lidar-robot-lawn-mowers" element={
                  <BlogErrorBoundary>
                    <RTKvsLiDARRobotLawnMowers2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-robotic-pool-cleaners-canada" element={
                  <BlogErrorBoundary>
                    <BestRoboticPoolCleanersCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/cordless-vs-corded-robotic-pool-cleaners" element={
                  <BlogErrorBoundary>
                    <CordlessVsCordedRoboticPoolCleaners2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/aiper-scuba-s1-vs-dolphin-nautilus-cc-plus" element={
                  <BlogErrorBoundary>
                    <AiperScubaS1VsDolphinNautilusCCPlus2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/ecovacs-ultramarine-p1-vs-aiper-scuba-s1" element={
                  <BlogErrorBoundary>
                    <EcovacsUltramarineP1VsAiperScubaS12026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/aiper-irrisense-2-vs-rain-bird-oto-canada" element={
                  <BlogErrorBoundary>
                    <AiperIrriSense2VsRainBirdOtoCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-robotic-pool-skimmers-canada" element={
                  <BlogErrorBoundary>
                    <BestRoboticPoolSkimmersCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-robot-window-cleaners-canada" element={
                  <BlogErrorBoundary>
                    <BestRobotWindowCleanersCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/are-robot-window-cleaners-safe" element={
                  <BlogErrorBoundary>
                    <AreRobotWindowCleanersSafe2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/winbot-w3-omni-vs-w2-pro-omni" element={
                  <BlogErrorBoundary>
                    <WinbotW3OmniVsW2ProOmni2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/winbot-w2s-omni-vs-w2-pro-omni" element={
                  <BlogErrorBoundary>
                    <WinbotW2sOmniVsW2ProOmni2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-self-cleaning-litter-boxes-canada" element={
                  <BlogErrorBoundary>
                    <BestSelfCleaningLitterBoxesCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/open-top-vs-enclosed-self-cleaning-litter-boxes" element={
                  <BlogErrorBoundary>
                    <OpenTopVsEnclosedSelfCleaningLitterBoxes2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/litter-robot-4-vs-neakasa-m1-plus" element={
                  <BlogErrorBoundary>
                    <LitterRobot4VsNeakasaM1Plus2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/litter-robot-4-vs-petkit-puramax-2-canada" element={
                  <BlogErrorBoundary>
                    <LitterRobot4VsPetkitPuraMax2Canada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/hydroflow-pearl-vs-pearl-plus-vs-hs40-canada" element={
                  <BlogErrorBoundary>
                    <HydroflowPearlVsPearlPlusVsHs40Canada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/ideal-basement-humidity-canada" element={
                  <BlogErrorBoundary>
                    <IdealBasementHumidityCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-basement-dehumidifiers-canada" element={
                  <BlogErrorBoundary>
                    <BestBasementDehumidifiersCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/35-pint-vs-50-pint-dehumidifier-canada" element={
                  <BlogErrorBoundary>
                    <ThreeFivePintVs50PintDehumidifier2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/dehumidifier-pump-vs-gravity-drain-canada" element={
                  <BlogErrorBoundary>
                    <DehumidifierPumpVsGravityDrain2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-crawl-space-dehumidifiers-canada" element={
                  <BlogErrorBoundary>
                    <BestCrawlSpaceDehumidifiersCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-battery-backup-sump-pumps-canada" element={
                  <BlogErrorBoundary>
                    <BestBatteryBackupSumpPumpsCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/zoeller-aquanot-fit-508-canada" element={
                  <BlogPublicationGuard publishedAt="2026-11-05" redirectTo="/country/canada/blog">
                    <BlogErrorBoundary>
                      <ZoellerAquanotFit508Canada2026 />
                    </BlogErrorBoundary>
                  </BlogPublicationGuard>
                } />
                <Route path="blog/battery-backup-vs-water-powered-sump-pump-canada" element={
                  <BlogErrorBoundary>
                    <BatteryBackupVsWaterPoweredSumpPump2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-water-powered-backup-sump-pumps-canada" element={
                  <BlogErrorBoundary>
                    <BestWaterPoweredBackupSumpPumpsCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-smart-sump-pump-monitors-canada" element={
                  <BlogErrorBoundary>
                    <BestSmartSumpPumpMonitorsCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-water-leak-detectors-canada" element={
                  <BlogErrorBoundary>
                    <BestWaterLeakDetectorsCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-automatic-water-shutoff-valves-canada" element={
                  <BlogErrorBoundary>
                    <BestAutomaticWaterShutoffValvesCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/moen-flo-vs-phyn-plus-canada" element={
                  <BlogErrorBoundary>
                    <MoenFloVsPhynPlusCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-radon-detectors-canada" element={
                  <BlogErrorBoundary>
                    <BestRadonDetectorsCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/digital-radon-monitor-vs-test-kit-canada" element={
                  <BlogErrorBoundary>
                    <DigitalRadonMonitorVsTestKit2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-portable-power-stations-winter-power-outages-canada" element={
                  <BlogErrorBoundary>
                    <BestPortablePowerStationsWinterCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/portable-power-station-size-winter-outage-canada" element={
                  <BlogErrorBoundary>
                    <PortablePowerStationSizeWinterOutage2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/portable-power-station-vs-generator-winter-outages-canada" element={
                  <BlogErrorBoundary>
                    <PortablePowerStationVsGenerator2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/anker-c1000-gen-2-vs-bluetti-elite-200-v2-canada" element={
                  <BlogErrorBoundary>
                    <AnkerC1000Gen2VsBluettiElite200V2Canada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-electric-garage-heaters-canadian-winters" element={
                  <BlogErrorBoundary>
                    <BestElectricGarageHeatersCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-portable-space-heaters-canadian-winters" element={
                  <BlogErrorBoundary>
                    <BestPortableSpaceHeatersCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-freeze-alarms-canadian-homes-cottages" element={
                  <BlogErrorBoundary>
                    <BestFreezeAlarmsCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-heated-snow-melting-mats-canadian-stairs-walkways" element={
                  <BlogErrorBoundary>
                    <BestHeatedSnowMeltingMatsCanada2026 />
                  </BlogErrorBoundary>
                } />
                <Route path="blog/best-pipe-heating-cables-canadian-winters" element={
                  <BlogPublicationGuard publishedAt="2026-08-14" redirectTo="/country/canada/blog">
                    <BlogErrorBoundary>
                      <BestPipeHeatingCablesCanada2026 />
                    </BlogErrorBoundary>
                  </BlogPublicationGuard>
                } />
                <Route path="blog/best-roof-gutter-de-icing-cables-canada" element={
                  <BlogPublicationGuard publishedAt="2026-08-14" redirectTo="/country/canada/blog">
                    <BlogErrorBoundary>
                      <BestRoofGutterDeIcingCablesCanada2026 />
                    </BlogErrorBoundary>
                  </BlogPublicationGuard>
                } />
                <Route path="blog/best-roof-rakes-canadian-winters" element={
                  <BlogPublicationGuard publishedAt="2026-08-15" redirectTo="/country/canada/blog">
                    <BlogErrorBoundary>
                      <BestRoofRakesCanada2026 />
                    </BlogErrorBoundary>
                  </BlogPublicationGuard>
                } />
                <Route path="blog/top_canadian_baby_products_2025" element={<Navigate to="../top-canadian-baby-products-2025" replace />} />
                <Route path="blog/sustainable_baby_products_canada" element={<Navigate to="../sustainable-baby-products-canada" replace />} />
                <Route path="blog/safe_baby_feeding_guide_canada" element={<Navigate to="../safe-baby-feeding-guide-canada" replace />} />
                <Route path="blog/:postSlug" element={
                  <BlogErrorBoundary>
                    <BlogPost />
                  </BlogErrorBoundary>
                } />
                <Route path="contact" element={<Contact />} />
                <Route path="unsubscribe" element={<Unsubscribe />} />
                <Route path="corporate-gifting" element={<CorporateGiftingCanada />} />
                {/* Curator Application */}
                <Route path="curator-application" element={<CuratorApplication />} />
                
                {/* Development diagnostic routes - ONLY in development environment */}
                {process.env.NODE_ENV === 'development' && (
                  <>
                    <Route path="test-global-hooks" element={<GlobalHooksTester />} />
                    <Route path="api-diagnostic" element={<APIEndpointDiagnostic />} />
                    <Route path="state-diagnostic" element={<StateEndpointDiagnostic />} />
                  </>
                )}
                
                {/* Product routes - MUST come before category routes for proper matching */}
                <Route path="products" element={<ProductList />} />
                
                {/* Category routes - these MUST come before generic :productId route */}
                <Route path="products/health-wellness" element={<ProductList />} />
                <Route path="products/beauty" element={<ProductList />} />
                <Route path="products/grocery-gourmet-foods" element={<ProductList />} />
                <Route path="products/clothing" element={<ProductList />} />
                <Route path="products/home-decor" element={<ProductList />} />
                <Route path="products/baby-products" element={<ProductList />} />
                <Route path="products/electronics" element={<ProductList />} />
                <Route path="products/sports-outdoors" element={<ProductList />} />
                <Route path="products/books" element={<ProductList />} />
                <Route path="products/toys-games" element={<ProductList />} />
                <Route path="products/pet-supplies" element={<ProductList />} />
                <Route path="products/automotive" element={<ProductList />} />
                <Route path="products/tools-hardware" element={<ProductList />} />
                <Route path="products/garden-outdoor" element={<ProductList />} />
                <Route path="products/jewelry" element={<ProductList />} />
                <Route path="products/music" element={<ProductList />} />
                <Route path="products/movies-tv" element={<ProductList />} />
                <Route path="products/video-games" element={<ProductList />} />
                <Route path="products/office-supplies" element={<ProductList />} />
                <Route path="products/kitchen-dining" element={<ProductList />} />
                
                {/* Product detail routes - these come AFTER category routes */}
                <Route path="products/:productId" element={<ProductDetail />} />
                <Route path="products/:productId/name/:productName" element={<ProductDetail />} />
                
                <Route path="search" element={<SearchPage />} />
                <Route path="discover" element={<AIDiscoverPage />} />
                <Route path="login" element={<Login />} />
                <Route path="favorites" element={<FavoritedProducts />} />
                
                {/* Collections routes */}
                <Route path="collections" element={<Collections />} />
                <Route path="collections/:slugAndId/edit" element={<CollectionEditor />} />
                <Route path="collections/:slugAndId" element={<PublicCollection />} />
                {/* Curator profile routes */}
                <Route path="curators/:handle" element={<CuratorProfile />} />
                
                {/* My Values preferences route */}
                <Route path="my/values" element={<MyValues />} />
                
                {/* Canadian Brands Hub routes - specific routes MUST come before dynamic :brandId route */}
                <Route path="brands" element={<CanadianBrandsHub />} />
                <Route path="brands/by-values" element={<CanadianBrandsByValues />} />
                <Route path="brands/values/:valueId" element={<CanadianBrandsByValue />} />
                <Route path="brands/:brandId" element={<BrandDetail />} />
                <Route path="brands/:brandId/:brandSlug" element={<BrandDetail />} />
                
                {/* Top Searches Hub routes - specific pages MUST come before clean URL category routes */}
                <Route path="top" element={<TopSearchesHub />} />
                <Route path="top/canadian-snack-boxes-care-packages" element={<CanadianSnackBoxes />} />
                <Route path="top/magnesium-glycinate-canada" element={<MagnesiumGlycinateCanada />} />
                <Route path="top/electrolyte-powders-canada" element={<ElectrolytePowdersCanada />} />
                
                {/* Hub routes - category hubs for Canada */}
                <Route path="hub" element={<HubIndex />} />
                <Route path="hub/clean-beauty" element={<CleanBeautyHub />} />
                <Route path="hub/wellness" element={<WellnessHub />} />
                <Route path="hub/low-waste-home" element={<LowWasteHomeHub />} />
                <Route path="hub/baby-kids" element={<BabyKidsHub />} />
                <Route path="hub/home-robots" element={<HomeRobotsHub />} />
                <Route path="hub/basement-moisture-control" element={<BasementMoistureControlHub />} />
                <Route path="hub/winter-home-preparedness" element={<WinterHomePreparednessHub />} />
                <Route path="baby-registry" element={<CanadaBabyRegistry />} />
                <Route path="baby-registry/:templateSlug" element={<CanadaBabyRegistryTemplatePreview />} />
                <Route path="baby-registry/preview/:templateSlug" element={<CanadaBabyRegistryTemplatePreview />} />
                <Route path="baby-registry/my" element={<CustomBabyRegistryPage />} />
                <Route path="baby-registry/shared/:shareKey" element={<CustomBabyRegistryPage />} />
                <Route path="hub/sustainable-brands" element={<SustainableBrandsHub />} />
                <Route path="hub/seasonal" element={<SeasonalHub />} />
                
                {/* Clean URL routes for categories - MUST come after specific routes */}
                <Route path=":category" element={<ProductList />} />
                <Route path=":category/:subcategory" element={<ProductList />} />
                {/* Development route for viewing user logs */}
                {getEnv('NODE_ENV') === 'development' && (
                  <Route path="user-logs" element={<UserLogsViewer />} />
                )}
                {/* AI Citation Analytics Dashboard (email-gated inside component) */}
                <Route path="ai-citations" element={<AICitationDashboard />} />
                {/* Development route for debugging One Tap */}
                {getEnv('NODE_ENV') === 'development' && (
                  <Route path="onetap-debug" element={<OneTapDebug />} />
                )}
                {/* Legal pages are not lazy loaded for better prerendering */}
                <Route path="privacy" element={<PrivacyPolicy />} />
                <Route path="disclaimer" element={<Disclaimer />} />
                <Route path="accessibility" element={<AccessibilityStatement />} />
                <Route path="affiliate-disclosure" element={<AffiliateDisclosure />} />
                <Route path="verification-policy" element={<VerificationPolicy />} />
              </Route>
              
              {/* Preserve unknown URLs and exclude the error page from search. */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </div>
        </ErrorBoundary>
      </HelmetProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
